import { computed } from 'mobx'
import { task } from 'mobx-task'
import { switchMap } from 'rxjs'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { toStream } from '@/utils/mobx-util'
import { withAbortSignal } from '@/utils/rx-util'
import { type Job } from '@/features/jobs/Job'
import type { Checklist } from '@/features/jobs/info-gathering/Checklist'
import {
  formatTaxRefundDeliveryMethodType,
  maskDigits,
  maskText,
} from '@/features/jobs/info-gathering/info-gathering-formatters'
import type { ProvidedTaxRefundMethod } from '@/features/jobs/info-gathering/supporting-item-input'
import { type TaxRefundMethodStore } from './TaxRefundMethodStore'
import type { TaxRefundMethodVersion } from './TaxRefundMethodVersion'
import type { PhysicalAddress } from '@/features/jobs/info-gathering/PhysicalAddress'
import { match } from '@/utils/pattern-match-util'

/**
 * The personal and business tax refund methods the client provided for a
 * job.
 */
export class TaxRefundMethodViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Loads the refund methods.
   */
  fetch = task(async (signal: AbortSignal) => {
    await this._fetchRefundMethods(signal)
  })

  constructor(
    private readonly _job: Job,
    private readonly _checklist: Checklist,
    private readonly _taxRefundMethodStore: TaxRefundMethodStore,
  ) {
    // Re-fetches the methods when another one is provided.
    this._lifecycle.manage(
      toStream(() => [
        this.personalRefundMethodSupportingItem?.input?.provided,
        this.businessRefundMethodSupportingItem?.input?.provided,
      ]).pipe(
        switchMap(() =>
          withAbortSignal((signal) =>
            // A failed refresh keeps showing the previously loaded value.
            this._fetchRefundMethods(signal).catch(() => undefined),
          ),
        ),
      ),
    )
  }

  /**
   * Whether either refund method has been provided.
   */
  @computed get isRelevant() {
    return Boolean(
      this.businessRefundMethodSupportingItem?.completed ||
      this.personalRefundMethodSupportingItem?.completed,
    )
  }

  /**
   * Whether both refund methods are requested, but neither was provided.
   */
  @computed get isBothEmpty() {
    return Boolean(
      this.personalRefundMethodSupportingItem &&
      this.businessRefundMethodSupportingItem &&
      !this.personalRefundMethodSupportingItem.completed &&
      !this.businessRefundMethodSupportingItem.completed,
    )
  }

  /**
   * Whether the provided refund methods have been fetched; editing before
   * then would start from a blank method and overwrite it.
   */
  @computed get isLoaded() {
    return (
      (!this.personalRefundMethodSupportingItem?.completed ||
        !!this.personalRefundMethod) &&
      (!this.businessRefundMethodSupportingItem?.completed ||
        !!this.businessRefundMethod)
    )
  }

  /**
   * The requested personal refund method supporting item.
   */
  @computed get personalRefundMethodSupportingItem() {
    return (
      this._checklist.personalInfoSection
        ?.personalRefundMethodInfoSupportingItem ?? null
    )
  }

  /**
   * The requested business refund method supporting item.
   */
  @computed get businessRefundMethodSupportingItem() {
    return (
      this._checklist.businessInfoSection
        ?.businessRefundMethodInfoSupportingItem ?? null
    )
  }

  /**
   * The provided personal refund method version.
   */
  @computed get personalRefundMethod() {
    return this._getRefundMethod(
      this.personalRefundMethodSupportingItem?.input?.provided,
    )
  }

  /**
   * The provided business refund method version.
   */
  @computed get businessRefundMethod() {
    return this._getRefundMethod(
      this.businessRefundMethodSupportingItem?.input?.provided,
    )
  }

  /**
   * The personal refund method, formatted for display.
   */
  @computed get personalRefundMethodDisplay() {
    return toTaxRefundMethodDisplay(this.personalRefundMethod)
  }

  /**
   * The business refund method, formatted for display.
   */
  @computed get businessRefundMethodDisplay() {
    return toTaxRefundMethodDisplay(this.businessRefundMethod)
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    // The section renders a failure from the task's state.
    void this.fetch(this._lifecycle.signal).catch(() => undefined)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  private _getRefundMethod(
    provided: ProvidedTaxRefundMethod | null | undefined,
  ) {
    if (!provided?.refundMethodId || !provided.refundMethodVersion) {
      return null
    }

    return (
      this._taxRefundMethodStore.getTaxRefundMethod(
        provided.refundMethodId,
        provided.refundMethodVersion,
      ) ?? null
    )
  }

  private async _fetchRefundMethods(signal: AbortSignal) {
    if (this.businessRefundMethodSupportingItem?.completed) {
      await this._taxRefundMethodStore.fetchBusinessTaxRefundMethodForJob(
        this._job.id,
        signal,
      )
    }

    if (this.personalRefundMethodSupportingItem?.completed) {
      await this._taxRefundMethodStore.fetchPersonalTaxRefundMethodForJob(
        this._job.id,
        signal,
      )
    }
  }
}

/**
 * A tax refund method formatted for display.
 */
export interface TaxRefundMethodDisplay {
  deliveryMethodType: string
  deliveryMethod: TaxRefundDeliveryMethodDisplay | null
}

/**
 * A tax refund delivery method formatted for display.
 */
export type TaxRefundDeliveryMethodDisplay =
  | {
      type: 'Check'
      mailingAddress: PhysicalAddress | null
      /**
       * The information whose address the check is mailed to when
       * `mailingAddress` is `null`.
       */
      sameAddressAs: 'personal' | 'business' | null
    }
  | {
      type: 'DirectDeposit'
      accountType: string
      routingNumber: string
      maskedRoutingNumber: string
      accountNumber: string
      maskedAccountNumber: string
      bankName: string
      maskedBankName: string
    }
  | { type: 'FutureTaxCredit' }

function toTaxRefundMethodDisplay(
  version: TaxRefundMethodVersion | null,
): TaxRefundMethodDisplay | null {
  if (!version) {
    return null
  }

  const deliveryMethod = version.deliveryMethod
  if (!deliveryMethod) {
    return { deliveryMethodType: '', deliveryMethod: null }
  }

  const deliveryMethodType = formatTaxRefundDeliveryMethodType(
    deliveryMethod.type,
  )
  switch (deliveryMethod.type) {
    case 'Check':
      return {
        deliveryMethodType,
        deliveryMethod: {
          type: 'Check',
          mailingAddress: deliveryMethod.mailingAddress,
          sameAddressAs: match(version.refundType, {
            Personal: () => 'personal' as const,
            Business: () => 'business' as const,
            Unspecified: () => null,
          }),
        },
      }
    case 'DirectDeposit': {
      const { bankAccount } = deliveryMethod
      return {
        deliveryMethodType,
        deliveryMethod: {
          type: 'DirectDeposit',
          accountType: bankAccount.accountType,
          routingNumber: bankAccount.routingNumber,
          maskedRoutingNumber: maskDigits(bankAccount.routingNumber),
          accountNumber: bankAccount.accountNumber,
          maskedAccountNumber: maskDigits(bankAccount.accountNumber),
          bankName: bankAccount.bankName,
          maskedBankName: maskText(bankAccount.bankName),
        },
      }
    }
    case 'FutureTaxCredit':
      return { deliveryMethodType, deliveryMethod: { type: 'FutureTaxCredit' } }
  }
}
